import { useState, useRef } from "react"
import { Link } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  FileText,
  Upload,
  Loader2,
  Download,
  Trash2,
  File as FileIcon,
  FileImage,
  Search,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  documentsApi,
  contactsApi,
  dealsApi,
  ApiError,
  type DocumentListItem,
} from "@/lib/api"
import { formatDateTime, timeAgo } from "@shared/utils/date"
import { useAuth } from "@/hooks/useAuth"

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function fileIcon(contentType: string) {
  if (contentType.startsWith("image/")) return FileImage
  return FileText
}

export type DocumentsListProps = {
  contactId?: string
  dealId?: string
  showContext?: boolean
  showUpload?: boolean
}

export function DocumentsList({
  contactId,
  dealId,
  showContext = false,
  showUpload = true,
}: DocumentsListProps) {
  const [uploadOpen, setUploadOpen] = useState(false)
  const [search, setSearch] = useState("")

  const query = useQuery({
    queryKey: ["documents", { contactId, dealId }],
    queryFn: () => documentsApi.list({ contactId, dealId, pageSize: 200 }),
  })

  const items = query.data?.items ?? []
  const filtered = search
    ? items.filter((d) => d.displayName.toLowerCase().includes(search.toLowerCase()))
    : items

  return (
    <div className="space-y-3">
      {showUpload && (
        <div className="flex items-center justify-between gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Search documents…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button onClick={() => setUploadOpen(true)}>
            <Upload className="h-4 w-4" />
            Upload
          </Button>
        </div>
      )}

      {query.isLoading ? (
        <Card className="grid place-items-center p-16">
          <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="bg-rc-blue-50 text-rc-blue-600 mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full">
            <FileText className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            {search ? "No matching documents" : "No documents yet"}
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            {showUpload
              ? "Upload PDFs, images, spreadsheets or Word docs (25MB max)."
              : "Documents will appear here once uploaded."}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-border">
            {filtered.map((doc) => (
              <DocumentRow key={doc.id} doc={doc} showContext={showContext} />
            ))}
          </ul>
        </Card>
      )}

      {showUpload && (
        <UploadDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          defaultContactId={contactId}
          defaultDealId={dealId}
        />
      )}
    </div>
  )
}

function DocumentRow({ doc, showContext }: { doc: DocumentListItem; showContext: boolean }) {
  const qc = useQueryClient()
  const { user } = useAuth()
  const Icon = fileIcon(doc.contentType)

  const download = useMutation({
    mutationFn: () => documentsApi.signedUrl(doc.id),
    onSuccess: (r) => {
      // Use a temporary anchor to trigger download
      window.location.href = r.url
    },
  })

  const remove = useMutation({
    mutationFn: () => documentsApi.remove(doc.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  })

  const canDelete =
    user &&
    (["admin", "manager"].includes(user.role) || doc.uploadedBy === user.id)

  const contactName =
    doc.contactBusinessName ??
    (doc.contactFirstName
      ? `${doc.contactFirstName} ${doc.contactLastName ?? ""}`.trim()
      : null)

  return (
    <li className="hover:bg-rc-grey-50 flex items-center gap-3 p-3">
      <Icon className="text-muted-foreground h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-foreground">{doc.displayName}</p>
          {doc.category && (
            <Badge variant="muted" className="text-[10px]">
              {doc.category}
            </Badge>
          )}
        </div>
        <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-xs">
          <span>{formatBytes(doc.sizeBytes)}</span>
          <span>·</span>
          <span title={formatDateTime(doc.createdAt)}>{timeAgo(doc.createdAt)}</span>
          {doc.uploaderName && (
            <>
              <span>·</span>
              <span>{doc.uploaderName}</span>
            </>
          )}
          {showContext && contactName && doc.contactId && (
            <>
              <span>·</span>
              <Link to={`/contacts/${doc.contactId}`} className="hover:text-foreground">
                {contactName}
              </Link>
            </>
          )}
          {showContext && doc.dealName && doc.dealId && (
            <>
              <span>·</span>
              <Link to={`/deals/${doc.dealId}`} className="hover:text-foreground">
                {doc.dealName}
              </Link>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => download.mutate()}
          disabled={download.isPending}
          title="Download"
        >
          {download.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        </Button>
        {canDelete && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (confirm(`Delete "${doc.displayName}"? This is a soft delete and can be restored by an admin.`)) {
                remove.mutate()
              }
            }}
            className="hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </li>
  )
}

function UploadDialog({
  open,
  onOpenChange,
  defaultContactId,
  defaultDealId,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  defaultContactId?: string
  defaultDealId?: string
}) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [contactId, setContactId] = useState(defaultContactId ?? "")
  const [dealId, setDealId] = useState(defaultDealId ?? "")
  const [category, setCategory] = useState("")
  const [displayName, setDisplayName] = useState("")
  const [notes, setNotes] = useState("")
  const [error, setError] = useState<string | null>(null)

  const contacts = useQuery({
    queryKey: ["contacts-picker"],
    queryFn: () => contactsApi.list({ pageSize: 500 }),
    enabled: open && !defaultContactId,
  })
  const deals = useQuery({
    queryKey: ["deals-picker"],
    queryFn: () => dealsApi.list({ pageSize: 500 }),
    enabled: open && !defaultDealId,
  })

  const upload = useMutation({
    mutationFn: (form: FormData) => documentsApi.upload(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] })
      onOpenChange(false)
      resetForm()
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Upload failed")
    },
  })

  const resetForm = () => {
    setCategory("")
    setDisplayName("")
    setNotes("")
    setError(null)
    if (fileRef.current) fileRef.current.value = ""
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const file = fileRef.current?.files?.[0]
    if (!file) {
      setError("Please choose a file")
      return
    }
    if (!contactId && !dealId) {
      setError("Must link to a contact or deal")
      return
    }
    const form = new FormData()
    form.set("file", file)
    if (contactId) form.set("contactId", contactId)
    if (dealId) form.set("dealId", dealId)
    if (category) form.set("category", category)
    if (displayName) form.set("displayName", displayName)
    if (notes) form.set("notes", notes)
    upload.mutate(form)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="file">File *</Label>
            <input
              ref={fileRef}
              id="file"
              type="file"
              required
              className="border-input mt-1 flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium"
              accept="application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv"
            />
            <p className="text-muted-foreground mt-1 text-xs">
              Max 25MB. PDF, images, Word, Excel, CSV, TXT.
            </p>
          </div>

          {!defaultContactId && (
            <div>
              <Label htmlFor="contactId">Contact</Label>
              <select
                id="contactId"
                className="border-input flex h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
              >
                <option value="">—</option>
                {contacts.data?.items.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.businessName ?? `${c.firstName} ${c.lastName}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!defaultDealId && (
            <div>
              <Label htmlFor="dealId">Deal</Label>
              <select
                id="dealId"
                className="border-input flex h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={dealId}
                onChange={(e) => setDealId(e.target.value)}
              >
                <option value="">—</option>
                {deals.data?.items.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.dealName ?? "Untitled"}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                placeholder="e.g. drivers-licence, contract"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                placeholder="(defaults to filename)"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={upload.isPending}>
              {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Upload
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================
// Global Documents page
// ============================================================
export default function DocumentsPage() {
  return (
    <AppShell title="Documents" subtitle="All documents across your organisation">
      <DocumentsList showContext showUpload />
    </AppShell>
  )
}

// Small helper for placement
void FileIcon
